import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string';
import {
	FGAInternalRole,
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { getVestingTermsSimplified } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useProgrammeUsers } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { useMipData } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useMipData.ts';
import {
	DEAL_ROUTES,
	MIP_MANAGE_ROUTES,
	MIP_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types.ts';
import { getMIPManageTabs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageTabs.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { SHOW_MIP_MANAGE_CONGRATULATION_MODAL } from './constants';

export const useMipManageController = () => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	const { deal, refetchDealData } = useContext(DealContext);

	const getVestingTermsRequest = useQuery({
		queryKey: [API_PATHS.MIP_VESTING_TERM_NAMES_GET],
		queryFn: ({ signal }) =>
			getVestingTermsSimplified(API_PATHS.MIP_VESTING_TERM_NAMES_GET, signal),
	});

	const navigate = useNavigate();

	const userProgrammeListQuery = useProgrammeUsers({
		programmeId: mipId,
	});

	const { data: mipData } = useMipData({ dealId, mipId });

	const { data: mipInvestorSettings, isLoading: isKycSettingLoading } = useMipKycSetting(
		mipId ?? ''
	);

	const disableSendInvestorInvitation = mipData?.isLocked;

	const programmeId = defaultToEmptyString(mipData?.id);

	const { hasPermission: hasKycAdminRole } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: programmeId,
		permissionRole: PermissionRole.KYC_ADMIN,
	});
	const { hasPermission: hasEditorRole } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: programmeId,
		permissionRole: PermissionRole.EDITOR,
	});
	const { hasPermission: hasExportRole } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: programmeId,
		permissionRole: PermissionRole.EXPORT_VIEWER,
	});
	const { hasPermission: hasAdminRole, isLoading: isAdminRoleLoading } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: programmeId,
		permissionRole: PermissionRole.ADMIN,
	});
	const showKycTab =
		isKycSettingLoading || (hasKycAdminRole && !!mipInvestorSettings?.isKycRequired);

	const { hasPermission: isDealstackSupport, isLoading: isSupportRoleLoading } =
		usePermissionRequest({ permissionRole: FGAInternalRole.DEALSTACK_SUPPORT });

	const { value: bankAccountStatusEnabled, loading: bankAccountStatusLoading } =
		useDealstackFeatureFlag(FEATURE_FLAG.BANK_ACCOUNT_STATUS);
	const isBankAccountsGateResolving =
		bankAccountStatusLoading || isAdminRoleLoading || isSupportRoleLoading || !programmeId;
	const showBankAccountsTab =
		!isBankAccountsGateResolving &&
		bankAccountStatusEnabled &&
		(hasAdminRole || isDealstackSupport);

	const MIPManageTabs: MantineTabProps<MIP_MANAGE_ROUTES>[] = useMemo(
		() => getMIPManageTabs(showKycTab, showBankAccountsTab),
		[showKycTab, showBankAccountsTab]
	);

	useEffect(() => {
		if (!mipData || mipData.state !== 'DRAFT') {
			return;
		}
		const path = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}`, {
			dealId,
			mipId,
		});
		navigate(path);
	}, [mipData, dealId, mipId]);

	useEffect(() => {
		return () => StorageUtilities.removeItem(StorageKeys.SHOW_COUNTERPARTY_NAMES_KEY);
	}, []);

	const [showMIPManageCongratulationsModal, setShowMIPManageCongratulationsModal] = useState(
		sessionStorage.getItem(formatUnicorn(SHOW_MIP_MANAGE_CONGRATULATION_MODAL, { mipId })) ===
			'true'
	);

	const markMIPManageCongratulationsModalAsSeen = () => {
		sessionStorage.removeItem(formatUnicorn(SHOW_MIP_MANAGE_CONGRATULATION_MODAL, { mipId }));
		setShowMIPManageCongratulationsModal(false);
	};

	return {
		userProgrammeList: userProgrammeListQuery.data ?? [],
		userProgrammeListQuery,
		dealId,
		mipId,
		disableSendInvestorInvitation,
		refetchDealData,
		deal,
		mipData,
		mipInvestorSettings,
		showMIPManageCongratulationsModal,
		markMIPManageCongratulationsModalAsSeen,
		MIPManageTabs,
		vestingTerms: getVestingTermsRequest.data?.payload ?? [],
		refetchVestingTerms: getVestingTermsRequest.refetch,
		hasEditorRole,
		hasExportRole,
		hasAdminRole,
		hasKycAdminRole,
		showBankAccountsTab,
		isBankAccountsGateResolving,
	};
};
